<template>
    <section>
        <v-form v-if="isVisible" @submit.prevent="processAddEditTimeCondition()">
            <v-toolbar color="primary" dark>
                <v-icon class="mr-3" v-text="icons.mdiPencilCircleOutline"></v-icon>
                <div v-text="isNightBreak ? $gettext('Edit Night Break') : $gettext('Edit Time Condition')"></div>
                <v-spacer></v-spacer>
                <v-icon v-text="icons.mdiClose" @click="hideForm" class="mr-2"></v-icon>
                <v-icon v-text="icons.mdiContentSave" @click.native.stop="processAddEditTimeCondition()"></v-icon>
            </v-toolbar>

            <v-card-text>
                <v-container>
                    <!-- Night Break specific fields -->
                    <template v-if="isNightBreak">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="startTime"
                                    :label="$gettext('From')"
                                    type="time"
                                    outlined
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="endTime"
                                    :label="$gettext('To')"
                                    type="time"
                                    outlined
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    v-model="selectedApps"
                                    :items="availableApps"
                                    item-text="appName"
                                    item-value="packageId"
                                    :label="$gettext('Allowed Apps During Night Break')"
                                    multiple
                                    chips
                                    outlined
                                ></v-select>
                            </v-col>
                        </v-row>
                    </template>

                    <!-- Regular group fields -->
                    <template v-else>
                        <v-row>
                            <v-col cols="12">
                                <v-switch
                                    v-model="flexible"
                                    :false-value="false"
                                    :true-value="true"
                                    class="mt-0 transition-ease-in-out"
                                    color="primary"
                                    inset
                                    :label="!flexible ? $gettext('Time based') : $gettext('Quota based')"
                                    persistent-hint
                                    :hint="!flexible ? $gettext('A fixed time window. e.g. from 4pm to 6pm only') : $gettext('Overall time allowed for a ') + (weekly ? $gettext('week') : $gettext('day')) + $gettext('. e.g. 2 hours anytime during a ') + (weekly ? $gettext('week') : $gettext('day'))"
                                ></v-switch>
                            </v-col>

                            <!-- Days Selection -->
                            <v-col cols="12">
                                <p class="subtitle-1 mb-0">{{ $gettext('Please pick the day(s)') }}</p>
                            </v-col>
                            <v-col cols="12" md="6">
                                <div class="days-selection">
                                    <v-checkbox
                                        v-for="(day, index) in firstHalfWeek"
                                        :key="index"
                                        v-model="selectedDays"
                                        :label="day.label"
                                        :value="day.value"
                                        color="primary"
                                        hide-details
                                        :class="{'mt-0': index === 0}"
                                        dense
                                    ></v-checkbox>
                                </div>
                            </v-col>
                            <v-col cols="12" md="6">
                                <div class="days-selection">
                                    <v-checkbox
                                        v-for="(day, index) in secondHalfWeek"
                                        :key="index"
                                        v-model="selectedDays"
                                        :label="day.label"
                                        :value="day.value"
                                        color="primary"
                                        hide-details
                                        :class="{'mt-0': index === 0}"
                                        dense
                                    ></v-checkbox>
                                </div>
                            </v-col>

                            <!-- Time based inputs -->
                            <v-col v-if="!flexible" cols="12" md="6">
                                <v-text-field
                                    v-model="scheduleFrom"
                                    :label="$gettext('From')"
                                    format="24hr"
                                    outlined
                                    type="time"
                                    dense
                                ></v-text-field>
                            </v-col>
                            <v-col v-if="!flexible" cols="12" md="6">
                                <v-text-field
                                    v-model="scheduleTo"
                                    :label="$gettext('To')"
                                    format="24hr"
                                    outlined
                                    type="time"
                                    dense
                                ></v-text-field>
                            </v-col>

                            <!-- Quota based inputs -->
                            <v-col v-if="flexible" cols="12" md="6">
                                <v-text-field
                                    v-model="timeAllowed"
                                    :label="$gettext('Overall Time Allowed (HH:MM)')"
                                    outlined
                                    placeholder="HH:MM"
                                    dense
                                ></v-text-field>
                            </v-col>
                            <v-col v-if="flexible" cols="12" md="6">
                                <v-select
                                    v-model="weekly"
                                    :items="quotaTypes"
                                    item-text="text"
                                    item-value="value"
                                    outlined
                                    :label="$gettext('Select the type of quota')"
                                    dense
                                ></v-select>
                            </v-col>

                            <!-- Strict End Switch -->
                            <v-col cols="12" md="6">
                                <v-switch
                                    v-model="strictEnd"
                                    :class="{'o-60': strictEnd !== 1}"
                                    :false-value="0"
                                    :true-value="1"
                                    class="mt-0 transition-ease-in-out"
                                    color="primary"
                                    inset
                                    label="Strict End"
                                ></v-switch>
                            </v-col>
                        </v-row>
                    </template>
                </v-container>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="hideForm">
                    {{ $gettext('Cancel') }}
                </v-btn>
                <v-btn color="primary" type="submit">
                    {{ $gettext('Save') }}
                </v-btn>
            </v-card-actions>
        </v-form>
    </section>
</template>

<script>
import { storeToRefs } from 'pinia'
import { usePhonesStore } from '@/stores/PhonesStore'
import { required, requiredIf } from 'vuelidate/lib/validators'
import MixinIcon from '@/mixins/MixinIcon'
import MixinStringManipulation from '@/mixins/MixinStringManipulation'
import MixinConfigRules from '@/mixins/database/MixinConfigRules'
import MixinLodashPadStart from '@/mixins/lodash/MixinLodashPadStart'

export default {
    name: 'TimeConditionEdit',
    mixins: [MixinIcon, MixinStringManipulation, MixinConfigRules, MixinLodashPadStart],
    
    setup() {
        const phonesStore = usePhonesStore()
        const { currentSelectedConfiguration } = storeToRefs(phonesStore)
        
        return {
            currentSelectedConfiguration,
            phonesStore
        }
    },

    data() {
        return {
            isVisible: false,
            isNightBreak: false,
            startTime: '',
            endTime: '',
            selectedApps: [],
            isEditing: false,
            currentId: null,
            addTimeConditionDays: [],
            addScheduleFrom: '',
            addScheduleTo: '',
            weekdays: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
            strictEnd: '',
            duration: 60,
            timeAllowed: '',
            flexible: false,
            weekly: false,
            selectedDays: [],
            scheduleFrom: '',
            scheduleTo: '',
            quotaTypes: [
                { text: 'Daily', value: false },
                { text: 'Weekly', value: true }
            ],
            firstHalfWeek: [
                { label: this.$gettext('Monday'), value: '1' },
                { label: this.$gettext('Tuesday'), value: '2' },
                { label: this.$gettext('Wednesday'), value: '3' },
                { label: this.$gettext('Thursday'), value: '4' }
            ],
            secondHalfWeek: [
                { label: this.$gettext('Friday'), value: '5' },
                { label: this.$gettext('Saturday'), value: '6' },
                { label: this.$gettext('Sunday'), value: '7' }
            ]
        }
    },

    created() {
        this.strictEnd = this.getDefaultStrictEndValue()
    },

    computed: {
        hasConfiguration() {
            return this.currentSelectedConfiguration && 
                   this.currentSelectedConfiguration.configurations && 
                   this.currentSelectedConfiguration.configurations[this.currentSelectedConfiguration.currentSelectedConfigurationIndex] !== undefined
        },

        currentConfiguration() {
            if (!this.hasConfiguration) return null
            return this.currentSelectedConfiguration.configurations[this.currentSelectedConfiguration.currentSelectedConfigurationIndex]
        }
    },

    methods: {
        showEditNightBreak(nightBreakData) {
            this.isVisible = true
            this.isNightBreak = true
            this.startTime = nightBreakData.startTime
            this.endTime = nightBreakData.endTime
            this.selectedApps = nightBreakData.apps || []
        },

        showEditGroup(groupData) {
            this.isVisible = true
            this.isNightBreak = false
            // ... populate regular group fields
        },

        processAddEditTimeCondition() {
            if (this.isNightBreak) {
                const nightBreakData = {
                    id: 'night_break',
                    ruleName: 'Night Break',
                    type: 'exclusive',
                    startTime: this.startTime,
                    endTime: this.endTime,
                    apps: this.selectedApps
                }
                this.phonesStore.updateTimeCondition(nightBreakData)
            } else {
                // Handle regular group save
            }
            this.hideForm()
        },

        hideForm() {
            this.isVisible = false
            this.isEditing = false
            this.currentId = null
            this.addTimeConditionDays = []
            this.addScheduleFrom = ''
            this.addScheduleTo = ''
            this.duration = 60
            this.strictEnd = this.getDefaultStrictEndValue()
            this.flexible = false
            this.timeAllowed = ''
            this.weekly = false
            this.selectedDays = []
        },

        extractDays(daysString) {
            return daysString?.split('').map(day => day) || []
        },

        isValidDuration(value) {
            const pattern = /^([0-9]{1,2}):([0-5][0-9])$/
            return pattern.test(value)
        },
    },

    validations: {
        // ... keep your existing validations
    }
}
</script>

<style lang="scss" scoped>
.time-condition-edit {
    padding: 16px;
    background-color: var(--v-background-base, #fff);
    border-radius: 4px;
    
    .days-selection {
        .v-input--selection-controls {
            margin-top: 0;
            padding-top: 0;
        }
    }
}

.transition-ease-in-out {
    transition: all 0.3s ease-in-out;
}

.o-60 {
    opacity: 0.6;
}
</style>
